import * as React from 'react'

import {
  AnswerFormFragment,
  ElementFragment,
  ElementOptionFragment,
} from 'generated/resources/graphql'

type RendererProps = {
  answer: AnswerFormFragment | undefined
  question: ElementFragment
}

type Renderer = (props: RendererProps) => {
  type: 'text' | 'dotTag' | 'longText' | 'tagList'
  value: {
    text?: React.ReactNode
    placeholder?: string
    label?: string
    color?: string
    tags?: Array<{ text: string; customColor: string }>
    lines?: number
  }
}

const DEFAULT_RENDER = {
  type: 'text' as const,
  value: {
    text: undefined,
    placeholder: '-',
  },
}

const getStatusVariant = (value: string) => {
  switch (value) {
    case '5':
      return 'viridian'
    case '4':
      return 'smoke'
    case '3':
      return 'yellow'
    case '2':
      return 'radical'
    case '1':
      return 'radical'
    default:
      return 'lilac'
  }
}

const RatingCell: Renderer = ({ answer, question }) => {
  if (!answer || !Array.isArray(answer.elementOptionIds) || answer.elementOptionIds.length === 0) {
    return DEFAULT_RENDER
  }

  const options = answer.elementOptionIds
    .map((optionId: string) =>
      question.options?.find(
        (opt: ElementOptionFragment) => opt.id.toString() === optionId.toString()
      )
    )
    .filter(Boolean)

  if (options.length === 0) {
    return DEFAULT_RENDER
  }

  const maxValue = question.options?.reduce(
    (max: number, opt: ElementOptionFragment) => Math.max(max, Number(opt.value)),
    0
  )

  const label = options
    .map((option: ElementOptionFragment) => `${option.label}: ${option.value}/${maxValue}`)
    .join(', ')
  const color = options.length ? getStatusVariant(String(options[0]?.value ?? '0')) : 'lilac'

  return {
    type: 'dotTag' as const,
    value: {
      label: label,
      color: color,
    },
  }
}

const SelectCell: Renderer = ({ answer, question }) => {
  if (!answer || !Array.isArray(answer.elementOptionIds) || answer.elementOptionIds.length === 0) {
    return DEFAULT_RENDER
  }

  const options =
    question.options?.filter((opt: ElementOptionFragment) =>
      answer.elementOptionIds?.includes(opt.id.toString())
    ) || []

  return {
    type: 'tagList' as const,
    value: {
      type: 'dot',
      tags: options.map((option) => ({
        text: option.label,
        customColor: '#fff',
      })),
    },
  }
}

const TextCell: Renderer = ({ answer }) => {
  if (!answer?.answer) {
    return DEFAULT_RENDER
  }

  return {
    type: 'longText' as const,
    value: {
      text: answer.answer,
      lines: 1,
    },
  }
}

export const cellByType: Record<string, Renderer> = {
  rating: RatingCell,
  score: RatingCell,
  select: SelectCell,
  multiselect: SelectCell,
  text: TextCell,
  number: TextCell,
  url: TextCell,
  dropdownsingle: SelectCell,
  dropdownmulti: SelectCell,
  date: TextCell,
  longtext: TextCell,
}
